import { Dispatch } from 'redux';
import { getAnnualEnrollmentInfo, getAnnualEnrollmentStatus, postAnnualEnrollment } from '../../../global/ducks/mlps-api';
import { YesNoIndicators } from '../../../global/models/api-model';
import { DemographicAppKeys } from '../../../global/models/demographics-model';
import { createNewForm } from '../../arc/constants/constant-strings';
import { DeclinedValue } from '../../arc/features/new-hire-enrollment/enrollment/models/coverage-models';
import { setAEInfo } from '../annual-enrollment-slice';
import { AnnualEnrollmentFormValues } from '../models/annual-enrollment-form-models';
import { AEInfo, AnnualEnrollmentState, ProcessingStatus } from '../models/annual-enrollment-models';

export function deleteWorkflow(dispatch: Dispatch<any>, app: DemographicAppKeys, guid: string, aeInfo: AEInfo, groupId?: number) {
	const abandonPayload = {
		form_info: {
			change_id: aeInfo?.form_info?.change_id,
			change_id_signature: aeInfo?.form_info?.change_id_signature,
			workflow_action: 'Delete',
		},
	};

	dispatch(postAnnualEnrollment(
		guid,
		abandonPayload,
		(response) => {
			if (response?.api_response?.status === 'OK') {
				dispatch(dispatch(setAEInfo(response, app)));
			}

		},
		null,
		groupId,
	));
}

export function fetchAnnualEnrollment(dispatch: Dispatch<any>, app: DemographicAppKeys, guid: string, skipStatus?: boolean, groupId?: number) {


	dispatch(getAnnualEnrollmentStatus(guid, (statusResponse) => {
		const status = statusResponse?.form_record?.processing_category;

		const manualNewFormInProgress = sessionStorage?.getItem(`${createNewForm}-${guid}`);

		const completedStatus =
			(status === ProcessingStatus.Completed) &&
				(app === DemographicAppKeys.myCPG && statusResponse?.form_record.processing_substatus !== ProcessingStatus.Abandon) &&
				!skipStatus &&
				manualNewFormInProgress !== YesNoIndicators.Yes
				? 1 : 0;

		dispatch(getAnnualEnrollmentInfo(
			guid,
			completedStatus,
			(getResponse) => {
				const aeInfoResponse = getResponse as AEInfo;

				if (aeInfoResponse?.form_record?.processing_status === ProcessingStatus.Active) {
					deleteWorkflow(dispatch, app, guid, aeInfoResponse, groupId);
				} else {
					dispatch(setAEInfo(getResponse, app));
				}
			},
			null,
			groupId,
		));
	}));
}


export function verifyAndSubmitAnnualEnrollment(dispatch: Dispatch<any>, app: DemographicAppKeys, guid: string, values: AnnualEnrollmentFormValues, aeState: AnnualEnrollmentState, callBack: () => void, groupId?: number, requireKaiserArbitration?: boolean) {
	const effectiveDate = aeState.AEInfo?.ae_status?.effective_date;
	// Use original effective dates from AEInfo when declining coverages
	const originalMedEffectiveDate = aeState?.AEInfo?.enrollments_base?.M?.[0]?.effective_date || effectiveDate;
	const originalDenEffectiveDate = aeState?.AEInfo?.enrollments_base?.D?.[0]?.effective_date || effectiveDate;
	const dependents = aeState.AEInfo!.eligible_dependents || [];
  const baseDeps = aeState.AEInfo!.dep_enrollments_base || {};
  let dependentsChanged = false;


	const dependentsPayload: Record<string, { M: YesNoIndicators; D: YesNoIndicators }> = {};
  dependents.forEach(dep => {
    const depGuid = dep.person._guid;
    const before = baseDeps[depGuid] || { M: YesNoIndicators.No, D: YesNoIndicators.No };
    const afterM = values.M.includes(depGuid) ? YesNoIndicators.Yes : YesNoIndicators.No;
    const afterD = values.D.includes(depGuid) ? YesNoIndicators.Yes : YesNoIndicators.No;

    dependentsPayload[depGuid] = { M: afterM, D: afterD };

    if (before.M !== afterM || before.D !== afterD) {
      dependentsChanged = true;
    }
  });


	const medKeepDeclined = values.medicalPlan === DeclinedValue && !aeState.AEInfo!.ae_defaults!.medical_default_plan;
	const medPlanChanged = !medKeepDeclined &&	values.medicalPlan !== aeState.AEInfo!.ae_defaults!.medical_default_plan;
	let medicalEdited = aeState.AEInfo!.enrollments_base!.M;
  if (medPlanChanged) {
	// use defaults if coverage was declined before, and it is staying declined
    medicalEdited = [{
      code: values.medicalPlan === DeclinedValue ? '' : values.medicalPlan,
			// If declining, keep the original effective_date; otherwise use AE effectiveDate for changes
			effective_date: values.medicalPlan === DeclinedValue ? originalMedEffectiveDate : effectiveDate,
      termination_date:
        values.medicalPlan !== DeclinedValue ? '' : effectiveDate,
    }];
  } else if (dependentsChanged) {
		medicalEdited = medicalEdited.map(m => ({
			...m,
			effective_date: effectiveDate,
		}));
  }
	
	const dentalKeepDeclined = values.dentalPlan === DeclinedValue && !aeState?.AEInfo?.ae_defaults?.dental_default_plan;
	const denPlanChanged = !dentalKeepDeclined && values.dentalPlan !== aeState?.AEInfo?.ae_defaults?.dental_default_plan;

	let dentalEdited = aeState.AEInfo!.enrollments_base!.D;
	if (denPlanChanged) {
		// use defaults if coverage was declined before, and it is staying declined
		dentalEdited = [{
			code: values.dentalPlan === DeclinedValue ? '' : values.dentalPlan,
			// If declining, keep the original effective_date; otherwise use AE effectiveDate for changes
			effective_date: values.dentalPlan === DeclinedValue ? originalDenEffectiveDate : effectiveDate,
			termination_date:
				values.dentalPlan !== DeclinedValue ? '' : effectiveDate,
		}];
	} else if (dependentsChanged) {
		    // plan stayed the same, but dependents changed
		dentalEdited = dentalEdited.map(d => ({
			...d,
			effective_date: effectiveDate,
		}));
	}

	const verifyPayload = {
		form_info: {
			change_id: aeState.AEInfo?.form_info?.change_id,
			change_id_signature: aeState.AEInfo?.form_info?.change_id_signature,
			workflow_action: 'Verify',
		},
		global_edited: {
			memo: values.memo,
			special_requests: values.specialRequests,
			date_of_qualifying_event: effectiveDate,
			user_acknowledgement: (values.agreement && (!requireKaiserArbitration || values.kaiserArbitrationAgreement)) ? YesNoIndicators.Yes : YesNoIndicators.No,
			decline_reason: values.declinedMedicalCoverageReason,
			decline_reason_other: values.declinedMedicalCoverageReasonOther,
		},
		enrollments_edited: {
			M: medicalEdited,
			D: dentalEdited,
		},
		dep_enrollments_edited: dependentsPayload,
	};

	dispatch(postAnnualEnrollment(
		guid,
		verifyPayload,
		(verifyResponse) => {
			const verifyResponseInfo = verifyResponse as AEInfo;

			const availableWorkflowActions = verifyResponseInfo?.form_info?.available_workflow_actions;
			const workflowAction = (availableWorkflowActions?.Process && 'Process') || (availableWorkflowActions?.Submit && 'Submit');

			if (verifyResponseInfo?.api_response?.status === 'OK' && verifyResponseInfo?.processing_messages?.errors?.length < 1) {
				const submitPayload = {
					form_info: {
						change_id: verifyResponseInfo?.form_info?.change_id,
						change_id_signature: verifyResponseInfo?.form_info?.change_id_signature,
						workflow_action: workflowAction,
					},
					global_edited: verifyPayload.global_edited,
					enrollments_edited: verifyPayload.enrollments_edited,
					dep_enrollments_edited: dependentsPayload,
				};

				dispatch(postAnnualEnrollment(
					guid,
					submitPayload,
					(submitResponse) => {
						const submitResponseInfo = submitResponse as AEInfo;

						dispatch(setAEInfo(submitResponseInfo, app));
						sessionStorage?.removeItem(`${createNewForm}-${guid}`);
						callBack();
					},
					null,
					groupId,
				));
			} else {
				dispatch(setAEInfo(verifyResponseInfo, app));
				callBack();
			}
		},
		null,
		groupId,
	));
}

